import { MenuItem, Snackbar, Alert } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setViewPanel } from '../../../app/approvalQueue/approvalQueueSlice';
import { RootState } from '../../../app/store';
import NoPermissionTooltip from '../../../components/Tooltips/NoPermissionTooltip';
import { useSignAndSubmit } from '../../../signing/useSignAndSubmit';
import { useSignatureRequirement } from '../../../signing/useSignatureRequirement';
import { isActionEnabled } from '../../utilities';
import ApproveModal from './ApproveModal';
import RejectModal from './RejectModal';
import { getFormViewRoute } from './viewPanelUtils';

type TActionsMenuProps = {
  handleClose: () => void;
};

function busyLabel(status: ReturnType<typeof useSignAndSubmit>['state']['status']) {
  switch (status) {
    case 'signing':
      return 'Signing…';
    case 'verifying':
      return 'Verifying…';
    case 'submitting':
    case 'submitting-signed':
      return 'Submitting…';
    default:
      return undefined;
  }
}

const ActionsMenu = (props: TActionsMenuProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedEntity, allowedActions } = useSelector(
    (state: RootState) => state.approvalQueue,
  );
  const user = useSelector((state: RootState) => state.user);

  const viewFormRoute = getFormViewRoute(
    selectedEntity?.objectType,
    selectedEntity?.objectId,
    selectedEntity?.workflowType,
  );

  const { state: signState, reset, submitApproval, submitRejection } =
    useSignAndSubmit();
  const { isSignatureRequired } = useSignatureRequirement();

  const isBusy =
    signState.status !== 'idle' &&
    signState.status !== 'success' &&
    signState.status !== 'error' &&
    signState.status !== 'wip';

  const [rejectModalOpen, setRejectModalOpen] = useState<boolean>(false);
  const [approveModalOpen, setApproveModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (signState.status === 'success') {
      setRejectModalOpen(false);
      setApproveModalOpen(false);
      props.handleClose();
      reset();
    }
  }, [signState.status, reset, props.handleClose]);

  const type = selectedEntity?.taskName?.toLowerCase().includes('internal')
    ? 'internally-approve'
    : 'federally-approve';

  const entityObjectType = selectedEntity?.objectType?.toLowerCase();
  // Prefer the backend-supplied per-phase role action (#1301); fall back to the
  // legacy tier-derived guess for non-role-gated rows (empty/absent field).
  const requiredAction =
    selectedEntity?.requiredRoleAction && selectedEntity.requiredRoleAction !== ''
      ? selectedEntity.requiredRoleAction
      : `${type}-${entityObjectType}`;
  // Approve/reject rights come from the user's role action, not from
  // whether their role can create the form via the Forms menu. Approvers
  // who can't create a TAR still need to approve one when it's in their
  // queue.
  const isApproveEnabled = !!isActionEnabled(allowedActions, requiredAction);

  const isItemCompleted =
    selectedEntity?.workflowState?.toLowerCase().includes('approved') ||
    selectedEntity?.workflowState?.toLowerCase().includes('rejected');

  const handleApprove = useCallback(
    (comment: string) => {
      if (!selectedEntity?.workflowId) return;
      const trimmed = comment.trim();
      void submitApproval({
        workflowId: selectedEntity.workflowId,
        taskNumber: selectedEntity.workflowTaskNumber ?? 0,
        comments: trimmed || `Approved by ${user.profile?.userName}`,
        signatureRequired: isSignatureRequired({
          workflowType: selectedEntity.workflowType,
          taskNumber: selectedEntity.workflowTaskNumber,
        }),
      });
    },
    [
      selectedEntity?.workflowId,
      selectedEntity?.workflowTaskNumber,
      selectedEntity?.workflowType,
      user.profile?.userName,
      submitApproval,
      isSignatureRequired,
    ],
  );

  const handleReject = useCallback(
    (comment: string) => {
      if (!selectedEntity?.workflowId) return;
      void submitRejection({
        workflowId: selectedEntity.workflowId,
        taskNumber: selectedEntity.workflowTaskNumber ?? 0,
        comments: comment,
        signatureRequired: isSignatureRequired({
          workflowType: selectedEntity.workflowType,
          taskNumber: selectedEntity.workflowTaskNumber,
        }),
      });
    },
    [
      selectedEntity?.workflowId,
      selectedEntity?.workflowTaskNumber,
      selectedEntity?.workflowType,
      submitRejection,
      isSignatureRequired,
    ],
  );

  if (isItemCompleted) {
    return [
      <MenuItem
        key="queue-view"
        onClick={() => {
          // Prefer opening the full form (read-only) when the row is a
          // form-backed object; otherwise open the side ViewPanel for
          // objects without a dedicated form page (Deliverable, Project).
          if (viewFormRoute) {
            navigate(viewFormRoute);
          } else {
            dispatch(setViewPanel(true));
          }
          props.handleClose();
        }}
      >
        View
      </MenuItem>,
    ];
  }

  return [
    <MenuItem
      key="queue-view"
      onClick={() => {
        // Prefer opening the full form (read-only) when the row is a
        // form-backed object; otherwise open the side ViewPanel for
        // objects without a dedicated form page (Deliverable, Project).
        if (viewFormRoute) {
          navigate(viewFormRoute);
        } else {
          dispatch(setViewPanel(true));
        }
        props.handleClose();
      }}
    >
      View
    </MenuItem>,
    <NoPermissionTooltip isDisabled={!isApproveEnabled} key={'approval-queue'}>
      <MenuItem
        key="approval-queue"
        onClick={() => setApproveModalOpen(true)}
        disabled={!isApproveEnabled || isBusy}
      >
        {isBusy ? (busyLabel(signState.status) ?? 'Approve') : 'Approve'}
      </MenuItem>
    </NoPermissionTooltip>,
    <NoPermissionTooltip isDisabled={!isApproveEnabled} key={'reject-queue'}>
      <MenuItem
        key="reject-queue"
        onClick={() => setRejectModalOpen(true)}
        disabled={!isApproveEnabled || isBusy}
      >
        Reject
      </MenuItem>
    </NoPermissionTooltip>,
    <RejectModal
      key="reject-modal"
      open={rejectModalOpen}
      onClose={() => {
        setRejectModalOpen(false);
        reset();
      }}
      onConfirm={handleReject}
      isLoading={isBusy}
      busyLabel={busyLabel(signState.status)}
    />,
    <ApproveModal
      key="approve-modal"
      open={approveModalOpen}
      onClose={() => {
        setApproveModalOpen(false);
        reset();
      }}
      onConfirm={handleApprove}
      isLoading={isBusy}
      busyLabel={busyLabel(signState.status)}
    />,
    // Approval/signing is still demo scaffolding, so any approve/reject error
    // surfaces a friendly "in development" toast (filled Alert, top-center —
    // the app-wide Snackbar convention) rather than a raw signing error.
    <Snackbar
      key="sign-error"
      open={signState.status === 'error'}
      autoHideDuration={2000}
      onClose={reset}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert severity="info" onClose={reset} variant="filled">
        This feature is currently in development.
      </Alert>
    </Snackbar>,
    <Snackbar
      key="sign-wip"
      open={signState.status === 'wip'}
      autoHideDuration={6000}
      onClose={reset}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="warning" onClose={reset} variant="filled">
        Work in Progress
      </Alert>
    </Snackbar>,
  ];
};

export default ActionsMenu;
